import React, { useState, useRef } from "react";
import { FiUpload, FiFile, FiX, FiLoader, FiCheckCircle, FiAlertCircle } from "react-icons/fi";
import { toast } from "react-toastify";
import { extractInvoiceData } from "../../services/invoiceExtractor.service";

const InvoiceUploader = ({ onDataExtracted, isDisabled = false, businessUuid }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [extractionStatus, setExtractionStatus] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'webp', 'tif', 'tiff'];
    const fileExtension = file.name.split('.').pop().toLowerCase();
    
    if (!validExtensions.includes(fileExtension)) {
      toast.error("Please upload a PDF, JPG, JPEG, PNG, WEBP, or TIFF file");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size should be less than 10MB");
      return;
    }

    if (!businessUuid) {
      toast.error("Business UUID is missing");
      return;
    }

    setSelectedFile(file);
    setUploading(true);
    setExtractionStatus(null);
    
    // Show upload progress
    const progressInterval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 500);

    try {
      // Call the backend API to extract invoice data
      const result = await extractInvoiceData(file, businessUuid);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      if (result.success && result.data) {
        setExtractionStatus("success");
        toast.success("Invoice data extracted successfully!");
        
        // Pass the extracted data to parent component
        if (onDataExtracted) {
          onDataExtracted(result.data);
        }
      } else {
        setExtractionStatus("error");
        toast.error(result.message || "Failed to extract invoice data");
      }
    } catch (error) {
      clearInterval(progressInterval);
      setExtractionStatus("error");
      
      // Show detailed error message
      const errorMsg = error.message || "Error processing invoice";
      toast.error(errorMsg);
      console.error("Extraction error:", error);
    } finally {
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
      }, 1000);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    const file = event.dataTransfer.files[0];
    if (file) {
      const syntheticEvent = { target: { files: [file] } };
      handleFileSelect(syntheticEvent);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setExtractionStatus(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const triggerFileInput = () => {
    if (!isDisabled && !uploading) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="w-full">
      <div
        onClick={triggerFileInput}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={`
          border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all
          ${extractionStatus === "success" ? "border-green-500 bg-green-50 dark:bg-green-900/20" : ""}
          ${extractionStatus === "error" ? "border-red-500 bg-red-50 dark:bg-red-900/20" : ""}
          ${!extractionStatus && !uploading ? "border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500" : ""}
          ${isDisabled || uploading ? "opacity-50 cursor-not-allowed" : ""}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.webp,.tif,.tiff,application/pdf,image/jpeg,image/jpg,image/png,image/webp,image/tiff"
          onChange={handleFileSelect}
          disabled={isDisabled || uploading}
          className="hidden"
        />
        
        {uploading ? (
          <div className="space-y-3">
            <FiLoader className="w-10 h-10 mx-auto text-blue-500 animate-spin" />
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Uploading and processing invoice...
            </p>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <p className="text-xs text-gray-500">{uploadProgress}%</p>
            <p className="text-xs text-gray-400">This may take a few moments...</p>
          </div>
        ) : extractionStatus === "success" ? (
          <div className="space-y-2">
            <FiCheckCircle className="w-10 h-10 mx-auto text-green-500" />
            <p className="text-sm text-green-600 dark:text-green-400 font-semibold">
              ✓ Invoice data extracted successfully!
            </p>
            {selectedFile && (
              <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                <FiFile className="w-3 h-3" />
                <span className="truncate max-w-[200px]">{selectedFile.name}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile();
                  }}
                  className="text-red-500 hover:text-red-700"
                >
                  <FiX className="w-3 h-3" />
                </button>
              </div>
            )}
            <p className="text-xs text-blue-500">Form has been populated with extracted data</p>
          </div>
        ) : extractionStatus === "error" ? (
          <div className="space-y-2">
            <FiAlertCircle className="w-10 h-10 mx-auto text-red-500" />
            <p className="text-sm text-red-600 dark:text-red-400">
              Extraction failed. Please try again.
            </p>
            <p className="text-xs text-gray-500">
              Make sure the invoice is clear and readable.
            </p>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeFile();
              }}
              className="text-xs text-blue-500 hover:text-blue-700 underline"
            >
              Try Again
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <FiUpload className="w-10 h-10 mx-auto text-gray-400" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Click or drag & drop to upload invoice
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Supports PDF, JPG, PNG, WEBP, TIFF (Max 10MB)
              </p>
              <p className="text-xs text-blue-500 mt-2">
                🔍 Extracts supplier details, products, prices, and taxes
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceUploader;